<script setup>
import { ref, computed, watch, } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import _ from 'lodash'
import UserService from '@/services/UserService'
import AppManageSecurity from './AppManageSecurity.vue'

// Props
const props = defineProps({
  subscriptions: Array
})

// Emit
const emit = defineEmits(['updateTitle'])

// Store and route
const store = useStore()
const route = useRoute()
const router = useRouter()

// State
const menu = ref([
  { name: 'account', href: '/manage/account' },
  // { name: 'change password', href: '/manage/change-password' },
  { name: 'LiveU API', href: '/manage/developers' },
  { name: 'Developer API', href: '/manage/api' },
  { name: 'vMix Token', href: '/manage/vmix-token' },
  { name: 'Captioning', href: '/manage/captioning' },
  { name: 'Overage', href: '/manage/overage' },
  { name: 'Security', href: '/manage/security' },
])

const userSubscription = ref(null)
const paidSub = ref(false)
const menuProcess = ref(false)
const userInformation = ref(null)
const isTeamOwner = ref(false)
const showSecurity = ref(false)

// Computed properties
const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const activeTeam = computed(() => store.state.User.activatedTeam)

// Methods
const checkBilingaccess = async (team) => {
  if (!team) {
    const hasCP = menu.value.some(element => element.name === 'change password')
    // if (!menuProcess.value && !hasCP) {
    //   menu.value.splice(3, 0, { name: 'change password', href: '/manage/change-password' })
    // }
  } else {
    const permission = await UserService.getMemberpermission(team, userInformation.value._id)
    let role = permission.role || null

    // if (role !== 'admin') {
    //   menu.value = menu.value.filter(item =>
    //     item.name !== 'billing' && item.name !== 'change password'
    //   )
    // }

    // if (role === 'admin') {
    //   const hasCP = menu.value.some(element => element.name === 'change password')
    //   if (!menuProcess.value && !hasCP) {
    //     menu.value.splice(3, 0, { name: 'change password', href: '/manage/change-password' })
    //   }
    // }
  }
}

const setUser = async () => {
  let userInfo = store.state.User
  if (!userInfo.activeTeam) {
    userInformation.value = userInfo
    isTeamOwner.value = true
  } else {
    let ownerUserInfo = await UserService.getTeamOwner(userInfo.activeTeam)
    isTeamOwner.value = ownerUserInfo.owner._id === userInfo._id
    ownerUserInfo.owner._id = userInfo._id
    ownerUserInfo.owner.activeTeam = userInfo.activeTeam
    userInformation.value = ownerUserInfo.owner
  }
  await checkBilingaccess(userInformation.value.activeTeam)
}

// Watchers
watch(activeTeam, () => {
  setUser()
}, {
  immediate: true
})

watch(() => route.path, (newPath) => {
  if (newPath === '/manage/security') {
    showSecurity.value = true
  } else {
    showSecurity.value = false
  }
}, {
  immediate: true
})

// Watch for setup state changes from security component
const showSecuritySetup = ref(false)

// 2FA Setup data
const twoFactorSetupData = ref({
  manualKey: '',
  backupCodes: [],
  qrCode: null
})

const isCurrentRoute = (item) => route.path === item.href

const handleBackFromSecurity = () => {
  showSecurity.value = false
  showSecuritySetup.value = false
  // Navigate back to the manage page
  router.push('/manage/security')
}

const handleSecuritySetupStart = (setupData) => {
  twoFactorSetupData.value = setupData
  showSecuritySetup.value = true
}

const handleSecuritySetupCancel = () => {
  showSecuritySetup.value = false
}

</script>

<template>
  <div class="container px-3 sm:px-4">
    <!-- Security Setup Override View -->
    <div v-if="showSecuritySetup">
      <AppManageSecurity 
        @back="handleBackFromSecurity" 
        @setup-start="handleSecuritySetupStart"
        @setup-cancel="handleSecuritySetupCancel"
        :show-setup="true"
        :setup-data="twoFactorSetupData"
      />
    </div>
    
    <!-- Standalone own-page route: renders its own PageHeader, no card chrome -->
    <div v-else-if="route.name === 'Team'" class="page-title-area">
      <router-view :user-subscription="userSubscription"></router-view>
    </div>

    <!-- Normal View -->
    <div v-else class="mt-4 lg:pt-8">
      <h1 class="text-xl sm:text-2xl font-medium mb-4 break-words">{{ route.meta.title }}</h1>
      <div class="min-w-0 bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-3 sm:p-4 md:p-5">
        <div v-if="route.name != 'SubscribeSubAccount' && route.name != 'Billing'"
          class="inline-flex items-center gap-1 bg-surface-1 rounded-lg p-1 overflow-x-auto max-w-full no-scrollbar mb-5">
          <router-link v-for="(menuItem, index) in menu" :key="index" :to="menuItem.href" draggable="false"
            class="capitalize text-sm font-medium text-surface-8 hover:text-white hover:no-underline hover:bg-surface-3 px-3 py-1.5 rounded-md flex items-center text-nowrap transition-all select-none"
            :class="isCurrentRoute(menuItem) ? '!bg-surface-4 !text-white shadow-sm' : ''">
            {{ menuItem.name }}
          </router-link>
        </div>

        <!-- Show security component when on security route -->
        <AppManageSecurity
          v-if="showSecurity"
          @back="handleBackFromSecurity"
          @setup-start="handleSecuritySetupStart"
          @setup-cancel="handleSecuritySetupCancel"
          :show-setup="false"
        />

        <!-- Show other router views when not on security route -->
        <router-view v-else :user-subscription="userSubscription"></router-view>
      </div>
    </div>
  </div>
</template>


<style scoped>
/* Keep existing styles */
</style>